import { PutWebsiteBuilderWebsiteVersionGlobalStyles, PutWebsiteBuilderWebsiteVersionPage, PutWebsiteBuilderWebsiteVersion } from "api/rpc/2024-04/facilityAdmin/websiteBuilder/websiteBuilder";
import { RootState } from "index";
import { AnyAction, Middleware } from "redux";
import { isWebsiteBuilderEditorHistoryAction, WebsiteBuilderEditorActionType } from "redux/actions/websiteBuilder/editor/websiteBuilderEditorAction";
import { StatusCode } from "api/protocols";
import { showError } from "redux/actions/ui";
import { ThunkDispatch } from "redux-thunk";
import axios, { CancelToken, CancelTokenSource } from "axios";
import { TWebsiteBuilderAutoSaveStatus } from "./websiteBuilderEditor";
import { websiteBuilderUpdateAutoSaveStatusState } from "redux/actions/websiteBuilder/editor/websiteBuilderEditor";
import { AuthActionTypes } from "redux/actions/actionTypes";

interface IWebsiteBuilderScrollToNodeDetail {
  id: string;
}

export class WebsiteBuilderScrollToNodeEvent extends CustomEvent<IWebsiteBuilderScrollToNodeDetail> {
  public static readonly TYPE = "wb:scrollToNode";

  constructor(detail: IWebsiteBuilderScrollToNodeDetail) {
    super(WebsiteBuilderScrollToNodeEvent.TYPE, {
      detail,
      bubbles: false,
      cancelable: false,
      composed: false,
    });
  }
}

const AUTO_SAVE_DEBOUNCE_DURATION = 5000;

class WebsiteBuilderAutoSaver {
  private source: CancelTokenSource = null;
  private timeout: number = null;

  constructor() {
  }

  public cancelAutoSave() {
    this.source?.cancel();
    this.source = null;

    clearTimeout(this.timeout);
    this.timeout = null;
  }

  public autoSave(
    viewingPastVersion: boolean,
    updateStatus: (updatedStatus: TWebsiteBuilderAutoSaveStatus) => void,
    save: (token: CancelToken) => Promise<StatusCode>,
    dispatchError: (status: StatusCode) => void,
  ) {
    if (viewingPastVersion) {
      return;
    }

    this.cancelAutoSave();

    updateStatus("unsaved-changes");

    this.source = axios.CancelToken.source();
    const sourceRef = this.source;

    this.timeout = window.setTimeout(async () => {
      updateStatus("saving");

      const status = await save(sourceRef.token);

      if (status !== StatusCode.OK) {
        if (!sourceRef.token.reason) {
          updateStatus("error");
          dispatchError(status);
        }
      } else {
        updateStatus("saved");
      }
    }, AUTO_SAVE_DEBOUNCE_DURATION);
  }
}

interface IWebsiteBuilderPageAutoSaverRequest {
  source: CancelTokenSource;
  timeout: number;
}

class WebsiteBuilderPageAutoSaver {
  private pageRequestMap: Map<number, IWebsiteBuilderPageAutoSaverRequest> = new Map();

  constructor() {
  }

  public cancelAllAutoSaves() {
    this.pageRequestMap.forEach(({ source, timeout }) => {
      source.cancel();
      clearTimeout(timeout);
    });

    this.pageRequestMap.clear();
  }

  private cancelAutoSave(pageId: number) {
    const request = this.pageRequestMap.get(pageId);

    if (request) {
      request.source.cancel();
      clearTimeout(request.timeout);

      this.pageRequestMap.delete(pageId);
    }
  }

  public autoSave(
    viewingPastVersion: boolean,
    pageId: number,
    updateStatus: (pageId: number, updatedStatus: TWebsiteBuilderAutoSaveStatus) => void,
    save: (token: CancelToken) => Promise<StatusCode>,
    dispatchError: (status: StatusCode) => void,
  ) {
    if (viewingPastVersion) {
      return;
    }

    this.cancelAutoSave(pageId);

    updateStatus(pageId, "unsaved-changes");

    const source = axios.CancelToken.source();
    const timeout = window.setTimeout(async () => {
      updateStatus(pageId, "saving");

      const status = await save(source.token);

      if (status !== StatusCode.OK) {
        if (!source.token.reason) {
          updateStatus(pageId, "error");
          dispatchError(status);
          this.pageRequestMap.delete(pageId);
        }
      } else {
        updateStatus(pageId, "saved");
        this.pageRequestMap.delete(pageId);
      }
    }, AUTO_SAVE_DEBOUNCE_DURATION);

    const request: IWebsiteBuilderPageAutoSaverRequest = {
      source,
      timeout,
    };

    this.pageRequestMap.set(pageId, request);
  }
}

const themeAutoSaver = new WebsiteBuilderAutoSaver();
const globalStylesAutoSaver = new WebsiteBuilderAutoSaver();
const globalLayoutAutoSaver = new WebsiteBuilderAutoSaver();
const pageAutoSaver = new WebsiteBuilderPageAutoSaver();

// eslint-disable-next-line @typescript-eslint/no-empty-object-type
export const websiteBuilderMiddleware: Middleware<{}, RootState, ThunkDispatch<RootState, unknown, AnyAction>> = api => next => (action: AnyAction) => {
  const result = next(action);

  if (
    action.type === AuthActionTypes.USER_LOGIN ||
    action.type === AuthActionTypes.USER_LOGOUT ||
    action.type === WebsiteBuilderEditorActionType.EXIT_EDITOR
  ) {
    themeAutoSaver.cancelAutoSave();
    globalStylesAutoSaver.cancelAutoSave();
    globalLayoutAutoSaver.cancelAutoSave();
    pageAutoSaver.cancelAllAutoSaves();
  } else if (action.type === WebsiteBuilderEditorActionType.SELECT_THEME) {
    const { editor } = api.getState().websiteBuilderStore;

    const updateStatus = (updatedStatus: TWebsiteBuilderAutoSaveStatus) => {
      api.dispatch(websiteBuilderUpdateAutoSaveStatusState({ theme: updatedStatus }));
    };

    const saveTheme = async (token: CancelToken): Promise<StatusCode> => {
      const res = await PutWebsiteBuilderWebsiteVersion({
        id: editor.websiteVersion?.id,
        theme_id: editor.theme?.id,
      }, false, token);

      return res.status;
    };

    const dispatchError = () => api.dispatch(showError("Error saving theme")); // TODO: Translation

    themeAutoSaver.autoSave(editor.viewingPastVersion, updateStatus, saveTheme, dispatchError);
  } else if (action.type === WebsiteBuilderEditorActionType.UPDATE_GLOBAL_STYLES) {
    const { editor } = api.getState().websiteBuilderStore;

    const updateStatus = (updatedStatus: TWebsiteBuilderAutoSaveStatus) => {
      api.dispatch(websiteBuilderUpdateAutoSaveStatusState({ globalStyles: updatedStatus }));
    };

    const globalStyles = editor.globalStyles;

    const saveGlobalStyles = async (token: CancelToken): Promise<StatusCode> => {
      const res = await PutWebsiteBuilderWebsiteVersionGlobalStyles({
        website_version_id: globalStyles?.website_version_id,
        heading_color: globalStyles?.heading_color,
        heading_font_id: globalStyles?.heading_font_id,
        body_text_color: globalStyles?.body_text_color,
        body_text_font_id: globalStyles?.body_text_font_id,
        body_text_font_size: globalStyles?.body_text_font_size,
        button_color: globalStyles?.button_color,
        button_background_color: globalStyles?.button_background_color,
        button_border_color: globalStyles?.button_border_color,
        button_hover_color: globalStyles?.button_hover_color,
        button_hover_background_color: globalStyles?.button_hover_background_color,
        button_hover_border_color: globalStyles?.button_hover_border_color,
        button_hover_transition: globalStyles?.button_hover_transition,
        link_color: globalStyles?.link_color,
        link_hover_color: globalStyles?.link_hover_color,
      }, false, token);

      return res.status;
    };

    const dispatchError = () => api.dispatch(showError("Error saving styles")); // TODO: Translation

    globalStylesAutoSaver.autoSave(editor.viewingPastVersion, updateStatus, saveGlobalStyles, dispatchError);
  } else if (isWebsiteBuilderEditorHistoryAction(action)) {
    const { editor } = api.getState().websiteBuilderStore;

    const updateGlobalLayoutStatus = (updatedStatus: TWebsiteBuilderAutoSaveStatus) => {
      api.dispatch(websiteBuilderUpdateAutoSaveStatusState({ globalLayout: updatedStatus }));
    };

    const saveGlobalLayout = async (token: CancelToken): Promise<StatusCode> => {
      const res = await PutWebsiteBuilderWebsiteVersion({
        id: editor.websiteVersion?.id,
        header: JSON.stringify(editor.header),
        footer: JSON.stringify(editor.footer),
      }, false, token);

      return res.status;
    };

    const dispatchGlobalLayoutError = (status: StatusCode) => {
      if (status === StatusCode.CONFLICT) {
        api.dispatch(showError("Editor version not compatible with server. Please refresh the page.")); // TODO: Translation
      } else {
        api.dispatch(showError("Error saving header and footer")); // TODO: Translation
      }
    };

    globalLayoutAutoSaver.autoSave(editor.viewingPastVersion, updateGlobalLayoutStatus, saveGlobalLayout, dispatchGlobalLayoutError);

    const actionPage = editor.pages.find(page => page.id === action.pageId);

    if (actionPage != null) {
      const updatePageStatus = (pageId: number, updatedStatus: TWebsiteBuilderAutoSaveStatus) => {
        api.dispatch(websiteBuilderUpdateAutoSaveStatusState({ page: { id: pageId, status: updatedStatus } }));
      };
  
      const savePage = async (token: CancelToken): Promise<StatusCode> => {
        const res = await PutWebsiteBuilderWebsiteVersionPage({
          id: actionPage.id,
          root: JSON.stringify(actionPage.root),
        }, false, token);
  
        return res.status;
      };
  
      const dispatchPageError = (status: StatusCode) => {
        if (status === StatusCode.CONFLICT) {
          api.dispatch(showError("Editor version not compatible with server. Please refresh the page.")); // TODO: Translation
        } else {
          api.dispatch(showError("Error saving page")); // TODO: Translation
        }
      };

      pageAutoSaver.autoSave(editor.viewingPastVersion, actionPage.id, updatePageStatus, savePage, dispatchPageError);

      if ((action.type === WebsiteBuilderEditorActionType.UNDO || action.type === WebsiteBuilderEditorActionType.REDO) && action.scrollNodeId != null) {
        requestAnimationFrame(() => {
          window.dispatchEvent(
            new WebsiteBuilderScrollToNodeEvent({
              id: action.scrollNodeId,
            }),
          );
        });
      }
    }
  }

  return result;
};